import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  notifyThirdPartyCartApps,
  relocateCartAppWidgets,
  installCartMutationWatcher,
  settleAndRefreshCartDrawer,
} from '@theme/cart-ui';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    installCartMutationWatcher();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleClearAllClick);
    this.#bindTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleClearAllClick);
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
  }

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {Promise<void> | null} */
  #refreshPromise = null;

  #bindTrigger() {
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.#trigger = this.querySelector('.cart-drawer__trigger, [data-testid="cart-drawer-trigger"]');
    this.#trigger?.addEventListener('click', this.#onTriggerClick);
  }

  /**
   * @param {Event} event
   */
  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.open();
  };

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    const data = /** @type {any} */ (event)?.detail?.data || {};
    const source = data.source || '';

    // Remove / quantity changes already morph in place
    if (source === 'cart-items-component' || source === 'cart-clear-all') {
      return;
    }

    // Show main product immediately from bundled sections
    this.#applySectionsHtml(data.sections);

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // xCotton adds Product Protection in a separate request AFTER the main product.
    // Wait until cart stops changing, then refresh once so both lines appear together.
    settleAndRefreshCartDrawer();
  };

  /**
   * @returns {boolean}
   */
  #isShowingEmpty() {
    const cartItems = this.querySelector('cart-items-component');
    return Boolean(
      cartItems?.querySelector('.cart-drawer__content--empty') ||
        cartItems?.querySelector('[data-cart-empty]')
    );
  }

  /**
   * @returns {number}
   */
  #badgeCount() {
    const icon = document.querySelector('cart-icon');
    if (icon && 'currentCartCount' in icon) {
      return Number(/** @type {any} */ (icon).currentCartCount) || 0;
    }
    try {
      const stored = sessionStorage.getItem('cart-count');
      if (stored) return Number(JSON.parse(stored).value) || 0;
    } catch (_) {
      /* ignore */
    }
    return 0;
  }

  /**
   * Keep dialog open across morphs.
   * @param {boolean} wasOpen
   */
  #ensureDialogOpen(wasOpen) {
    if (!wasOpen) return;

    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    try {
      dialog.showModal();
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * Apply cart-items HTML from a sections map (cart/add response).
   * @param {Record<string, string> | undefined} sections
   * @returns {boolean} true when morph succeeded and drawer is not empty
   */
  #applySectionsHtml(sections) {
    if (!sections) return false;

    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;
    if (!cartItems || !sectionId) return false;

    const html =
      sections[sectionId] ||
      Object.values(sections).find(
        (value) => typeof value === 'string' && value.includes('cart-items-component')
      );

    if (typeof html !== 'string' || !html) return false;

    const dialog = this.querySelector('dialog');
    const wasOpen = Boolean(dialog instanceof HTMLDialogElement && dialog.open);

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newCartItems =
      fragment.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
      fragment.querySelector('cart-items-component');

    if (!newCartItems) return false;

    morph(cartItems, newCartItems);

    const nextDialog = this.querySelector('dialog');
    if (nextDialog) {
      const isEmpty = Boolean(
        cartItems.querySelector('.cart-drawer__content--empty') ||
          cartItems.querySelector('[data-cart-empty]')
      );
      nextDialog.classList.toggle('cart-drawer--empty', isEmpty);
      nextDialog.classList.toggle('is-loading', false);
    }

    this.#ensureDialogOpen(wasOpen);
    return !this.#isShowingEmpty();
  }

  /**
   * Re-fetch header section HTML and morph only cart-items-component.
   * @returns {Promise<void>}
   */
  #refreshCartItems = () => {
    if (this.#refreshPromise) return this.#refreshPromise;

    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;
    if (!cartItems || !sectionId) return Promise.resolve();

    const dialog = this.querySelector('dialog');
    const wasOpen = Boolean(dialog instanceof HTMLDialogElement && dialog.open);

    this.#refreshPromise = sectionRenderer
      .getSectionHTML(sectionId, false)
      .then((html) => {
        if (!this.isConnected || !html) return;

        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const newCartItems =
          fragment.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
          fragment.querySelector('cart-items-component');

        if (!newCartItems) return;

        morph(cartItems, newCartItems);

        const nextDialog = this.querySelector('dialog');
        if (nextDialog) {
          const isEmpty = Boolean(
            cartItems.querySelector('.cart-drawer__content--empty') ||
              cartItems.querySelector('[data-cart-empty]')
          );
          nextDialog.classList.toggle('cart-drawer--empty', isEmpty);
          nextDialog.classList.toggle('is-loading', false);
        }

        this.#ensureDialogOpen(wasOpen);
        relocateCartAppWidgets();
        notifyThirdPartyCartApps();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#refreshPromise = null;
      });

    return this.#refreshPromise;
  };

  /**
   * Public: refresh drawer HTML from server (used when apps mutate the cart).
   * @returns {Promise<void>}
   */
  refreshFromServer = () => this.#refreshCartItems();

  /** @type {boolean} */
  #clearingAll = false;

  /**
   * Clear cart in-place (keep drawer open, no page reload).
   * @param {MouseEvent} event
   */
  #handleClearAllClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cart-clear-all]') : null;
    if (!trigger || !this.contains(trigger) || this.#clearingAll) return;

    event.preventDefault();
    event.stopPropagation();

    this.#clearingAll = true;
    if (trigger instanceof HTMLButtonElement) trigger.disabled = true;

    const dialog = this.refs.dialog;
    const wasOpen = Boolean(dialog instanceof HTMLDialogElement && dialog.open);
    dialog?.classList.add('is-loading');

    fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(() => {
        try {
          sessionStorage.removeItem('cart-count');
          sessionStorage.setItem('cart-count', JSON.stringify({ value: '0', timestamp: Date.now() }));
        } catch (_) {
          /* ignore */
        }

        document.dispatchEvent(
          new CartUpdateEvent({}, 'cart-clear-all', {
            itemCount: 0,
            source: 'cart-clear-all',
          })
        );

        // Morph empty state in place; keep drawer open (no reload).
        return this.#refreshCartItems();
      })
      .then(() => {
        this.#ensureDialogOpen(wasOpen);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#clearingAll = false;
        dialog?.classList.remove('is-loading');
        // Button may have been morphed away with empty state
        if (trigger instanceof HTMLButtonElement && trigger.isConnected) {
          trigger.disabled = false;
        }
      });
  };

  /**
   * Backdrop click — DialogComponent already closes with a pointerdown guard.
   * Keep this no-op so Remove/clear morphs cannot close via the liquid binding.
   * @param {MouseEvent} _event
   */
  closeDialogOnClickOutside = (_event) => {};

  /**
   * @param {KeyboardEvent} event
   */
  closeDialogOnEscapePress = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };

  open = async () => {
    const dialog = this.refs.dialog;
    const needsContent = this.#isShowingEmpty() && this.#badgeCount() > 0;

    if (needsContent) {
      dialog?.classList.add('is-loading');
      this.showDialog();
      await (this.#refreshPromise ?? this.#refreshCartItems());
      dialog?.classList.remove('is-loading');
    } else {
      this.showDialog();
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  };

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
